// ── Report Engine ─────────────────────────────────────────────────────────────
// Single Source of Truth fuer alle Report-Zahlen: Berichte-Screen, Dashboard und
// PDF/Excel-Export rufen alle nur noch buildReport() auf. Damit ist garantiert:
//
//   was im Hero steht  =  was die PDF zeigt  =  was das Dashboard anzeigt.
//
// Reine ES-Module-Funktion. Keine Browser-/Firebase-Deps — laeuft auch
// serverseitig in den Export-API-Funktionen.
//
// Input:  { txs, year, isDE, marketData? }
// Output: striktes Report-Objekt (siehe JSDoc unten)
//
// Konvention "Brutto-Erloes / grossProceeds":
//   - verkauf: tx.preis (EUR-Erloes vor Gebuehrenabzug)
//   - swap:    effectiveErloes ?? tx.preis  (Stablecoin-Auto-Resolve wird gehonoriert)
//   Gebuehren werden separat ausgewiesen (totalFees), nicht von grossProceeds abgezogen.
//   "Was hat der Kunde durch Verkaeufe+Swaps ausbezahlt bekommen" = grossProceeds.
//
// Steuerlogik bleibt identisch zu fifo.js — wir konsumieren nur dessen Output.

import { buildFifoPool, getTaxableEvents } from './fifo';
import { buildAustrianPool } from './austrianCost';
import { rulesFor } from './taxRules';
import { INCOME_TYPES } from './transactionTypes';
import { COIN_MAP } from './coingecko';

const SELL_LIKE = new Set(['verkauf', 'swap']);

// Echte Daten-Luecke pro Symbol: Erwerbs-TXs (kauf/einzahlung-mit-preis/swap-IN)
// ohne exchange-Feld. Nur DANN ist ein "FIFO musste wallet-uebergreifend matchen"-
// Hinweis fuer den Kunden sinnvoll. Saubere Transfers zwischen Wallets, die FIFO
// global aufloest, sind KEIN Daten-Problem — und sollen entsprechend KEINEN Hinweis
// triggern. Spiegelt die Policy aus exportData.js#hasIncompleteExchangeDataForSym.
function hasMissingExchangeForSym(txs, sym) {
  const target = String(sym).toUpperCase();
  return (txs || []).some(tx => {
    const coinSym = String(tx.coin ?? tx.sym ?? '').toUpperCase();
    const coinInSym = String(tx.coinIn ?? tx.symIn ?? '').toUpperCase();
    const isErwerb =
      (tx.typ === 'kauf' && coinSym === target && Number(tx.preis) > 0) ||
      (tx.typ === 'einzahlung' && coinSym === target && Number(tx.preis) > 0) ||
      (tx.typ === 'swap' && coinInSym === target);
    if (!isErwerb) return false;
    const loc = String(tx.exchange || tx.platform || '').trim();
    return !loc;
  });
}

function yearOf(ev) {
  return (ev.datum || ev.effectiveTimestamp || '').slice(0, 4);
}

function symOfEvent(ev) {
  // Bei swap ist coin nicht gesetzt — nur coinOut/coinIn.
  // Wir buchen das Event auf den disposed-Coin (coinOut), nicht coinIn.
  return ev.coin || ev.coinOut || null;
}

function amountOfEvent(ev) {
  if (ev.typ === 'swap') return Number(ev.mengeOut) || 0;
  return Number(ev.menge) || 0;
}

// Fiat-Quote für Swap-Erloese: wenn die IN-Seite eines Swaps EUR/USD/... ist,
// ist die mengeIn direkt der Brutto-Erloes in Fiat — kein Resolver nötig.
const FIAT_QUOTES = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

// EUR-Bruttoerloes eines Verkaufs- oder Swap-Events (vor Gebuehr).
// Fuer swap: 1) Fiat-Quote (XLM→EUR) → mengeIn direkt, 2) effectiveErloes vom
// Stablecoin-Resolver, 3) preis als Fallback. Fuer verkauf direkt preis.
function grossOfEvent(ev) {
  if (ev.typ === 'swap') {
    const coinIn = String(ev.coinIn || ev.symIn || '').toUpperCase();
    if (FIAT_QUOTES.has(coinIn)) {
      const mIn = Number(ev.mengeIn);
      if (Number.isFinite(mIn) && mIn > 0) return mIn;
    }
    const eff = Number(ev.effectiveErloes);
    if (Number.isFinite(eff) && eff > 0) return eff;
  }
  const p = Number(ev.preis);
  return Number.isFinite(p) && p > 0 ? p : 0;
}

function feeOfEvent(ev) {
  const g = Number(ev.gebuehr);
  return Number.isFinite(g) && g > 0 ? g : 0;
}

// Liefert true, wenn das Event in das gewuenschte Jahr faellt. Filter laeuft
// nach dem FIFO-Lauf — wir muessen den ganzen Pool laufen lassen, damit
// Spekulationsfrist und ak korrekt sind, und filtern dann auf das Jahr.
// Wenn year === 'all' oder null, kein Filter (Dashboard-Modus).
function inYear(ev, year) {
  if (year == null || year === 'all') return true;
  return yearOf(ev) === String(year);
}

/**
 * Baut den Report fuer ein Jahr.
 *
 * @param {Object} params
 * @param {Array}  params.txs        Rohe Transaktionen aus Firestore
 * @param {number|string} params.year Berichtsjahr
 * @param {boolean} [params.isDE]    Deutschland-Modus (Spekulationsfrist, 26% Schaetzung)
 * @param {Object} [params.marketData] Optional fuer Coin-Logos
 *
 * @returns {Report}
 */
export function buildReport({ txs, year, isDE = false, country = null, marketData = null, prebuiltFifo = null }) {
  // Jurisdiktions-Profil: country gewinnt, sonst Rückfall auf isDE (DE) bzw.
  // DEFAULT (Protokoll-Modus). DE-Verhalten bleibt damit byte-genau.
  const rules = rulesFor(country || (isDE ? 'DE' : ''));
  const useAverage = rules.costBasis === 'average';

  // prebuiltFifo: { results, pool, transferPairs } — wenn der Aufrufer FIFO
  // bereits gebaut hat (z.B. Dashboard mit calcPortfolio + buildReport), den
  // Lauf nicht doppelt machen. Für AT (gleitender Durchschnitt) wird die
  // Österreich-Engine genutzt; prebuiltFifo (FIFO-Pool) ist dort nicht passend.
  const { results, pool, transferPairs } = useAverage
    ? { ...buildAustrianPool(txs || []), transferPairs: [] }
    : (prebuiltFifo ?? buildFifoPool(txs || []));
  const allTaxable = getTaxableEvents(results);
  const yearTaxable = allTaxable.filter(ev => inYear(ev, year));

  // Einkünfte (Staking/Mining/Lending …) — nur AT-Modell: Zufluss zum Tarif,
  // SEPARAT von der Kapital-Realisierung erfasst (nicht Teil der 27,5 %-Schätzung).
  let incomeEUR = 0;
  if (useAverage) {
    for (const ev of results) {
      if (INCOME_TYPES.has(ev.typ) && inYear(ev, year)) incomeEUR += Number(ev.incomeEUR) || 0;
    }
  }

  // Aggregationen ueber das Jahr.
  let grossProceeds = 0;        // brutto, vor Gebuehr (verkauf + swap)
  let totalFees = 0;            // Gebuehren auf verkauf + swap
  let costBasis = 0;            // ak summiert
  let realizedPnL = 0;          // gv summiert (negativ bei Verlust)
  let taxableGain = 0;          // gv summiert wo stpfl=true
  let freeGain = 0;             // gv summiert wo stpfl=false
  let eventsCount = 0;          // Anzahl steuer-relevanter Events
  let hasCostBasisUnknown = false;
  let hasFallbackUsed = false;
  let hasIncomplete = false;

  // Per-Asset-Map. Key = Coin-Symbol (disposed-Coin).
  const perAssetMap = new Map();

  function ensureAsset(sym) {
    if (!sym) return null;
    let a = perAssetMap.get(sym);
    if (!a) {
      const coinId = COIN_MAP[sym]?.id || null;
      a = {
        sym,
        name: COIN_MAP[sym]?.name || sym,
        coinId,
        image: marketData?.[coinId]?.image || null,
        grossProceeds: 0,
        sellGross: 0,        // nur typ === 'verkauf' (echte Fiat-Ausz.)
        swapGross: 0,        // nur typ === 'swap' (Token-zu-Token, kein Fiat)
        totalFees: 0,
        costBasis: 0,
        realizedPnL: 0,
        taxableGain: 0,
        freeGain: 0,
        amount: 0,
        eventsCount: 0,
        hasCostBasisUnknown: false,
        hasFallbackUsed: false,
        hasIncomplete: false,
        events: [],  // gefuellt unten (chronologisch sortiert)
      };
      perAssetMap.set(sym, a);
    }
    return a;
  }

  for (const ev of yearTaxable) {
    const sym = symOfEvent(ev);
    if (!sym) continue;  // null-coin-Guard (Hypothese 4 aus Audit)

    const isSellLike = SELL_LIKE.has(ev.typ);
    const gross = isSellLike ? grossOfEvent(ev) : 0;
    const fee = isSellLike ? feeOfEvent(ev) : 0;
    const ak = Number(ev.ak) || 0;
    const gv = Number(ev.gv) || 0;

    grossProceeds += gross;
    totalFees += fee;
    costBasis += ak;
    realizedPnL += gv;
    if (ev.stpfl) taxableGain += gv; else freeGain += gv;
    eventsCount += 1;
    // Nur als "echte" Daten-Luecke flaggen, wenn fuer DIESES Symbol auch ein
    // Erwerb ohne exchange-Feld existiert. Wallet-uebergreifendes FIFO bei
    // sauberen Daten ist KEIN Problem und soll keinen Hinweis triggern.
    const symHasGap = hasMissingExchangeForSym(txs, sym);
    if (ev.costBasisUnknown) hasCostBasisUnknown = true;
    if (ev.fallbackUsed && symHasGap) hasFallbackUsed = true;
    if (ev.incomplete) hasIncomplete = true;

    const a = ensureAsset(sym);
    a.grossProceeds += gross;
    if (ev.typ === 'verkauf') a.sellGross += gross;
    else if (ev.typ === 'swap') a.swapGross += gross;
    a.totalFees += fee;
    a.costBasis += ak;
    a.realizedPnL += gv;
    if (ev.stpfl) a.taxableGain += gv; else a.freeGain += gv;
    a.amount += amountOfEvent(ev);
    a.eventsCount += 1;
    if (ev.costBasisUnknown) a.hasCostBasisUnknown = true;
    if (ev.fallbackUsed && symHasGap) a.hasFallbackUsed = true;
    if (ev.incomplete) a.hasIncomplete = true;

    // Drilldown-Event: schlankes Format. soldLots bleibt drin fuer FIFO-Erklaerung.
    a.events.push({
      id: ev._docId || ev.id,
      datum: ev.datum,
      zeit: ev.zeit,
      effectiveTimestamp: ev.effectiveTimestamp,
      typ: ev.typ,
      coin: ev.coin,
      coinOut: ev.coinOut,
      coinIn: ev.coinIn,
      menge: ev.menge,
      mengeOut: ev.mengeOut,
      mengeIn: ev.mengeIn,
      preis: ev.preis,
      effectiveErloes: ev.effectiveErloes,
      gebuehr: ev.gebuehr,
      gross,
      fee,
      ak,
      gv,
      hf: ev.hf,
      stpfl: !!ev.stpfl,
      kaufDatum: ev.kaufDatum,
      costBasisMethod: ev.costBasisMethod,
      altvermoegen: ev.altvermoegen,
      exchange: ev.exchange,
      costBasisUnknown: !!ev.costBasisUnknown,
      incomplete: !!ev.incomplete,
      fallbackUsed: !!ev.fallbackUsed,
      priceSource: ev.priceSource,
      soldLots: ev.soldLots || [],
    });
  }

  // Per-Asset-Liste: nach Brutto-Erloes absteigend (was am meisten ausbezahlt wurde,
  // zuerst). Bei Gleichstand Realized PnL als Tiebreaker.
  const perAsset = [...perAssetMap.values()]
    .map(a => ({
      ...a,
      events: a.events.sort((x, y) => {
        const dx = x.effectiveTimestamp || x.datum || '';
        const dy = y.effectiveTimestamp || y.datum || '';
        return dx < dy ? -1 : dx > dy ? 1 : 0;
      }),
    }))
    .sort((x, y) => {
      if (y.grossProceeds !== x.grossProceeds) return y.grossProceeds - x.grossProceeds;
      return Math.abs(y.realizedPnL) - Math.abs(x.realizedPnL);
    });

  // Warnings — proaktive Hinweise, damit der Kunde versteht warum Zahlen
  // moeglicherweise unvollstaendig sind. Werden in der UI als gelbe Karte
  // angezeigt und vom Drilldown referenziert.
  const warnings = [];
  if (hasCostBasisUnknown) {
    const affected = perAsset.filter(a => a.hasCostBasisUnknown).map(a => a.sym);
    warnings.push({
      kind: 'cost-basis-unknown',
      message: 'Bei einigen Ereignissen fehlt die Anschaffungsbasis. Realisierter Gewinn dort nicht ermittelbar.',
      symbols: affected,
    });
  }
  if (hasIncomplete) {
    warnings.push({
      kind: 'incomplete-match',
      message: 'Bei einigen Verkaeufen/Swaps konnte FIFO nicht alle Anteile matchen — vermutlich fehlt eine Einkaufs-Transaktion.',
    });
  }
  if (hasFallbackUsed) {
    warnings.push({
      kind: 'wallet-fallback',
      message: 'FIFO musste fuer einzelne Ereignisse Wallet-uebergreifend matchen (Daten unvollstaendig). Berechnung bleibt korrekt, aber das Wallet-spezifische Detail ist ungenauer.',
    });
  }

  // Orphan pendingTransfer detection — eigentlich wird das durch buildFifoPool
  // implizit via completeTransfer aufgeloest. Wir scannen den finalen Pool aber
  // defensive auf Reste (vgl. Audit-Hypothese 1). Wenn pending > 0 bleibt,
  // wurden Coins reserviert aber nie restituiert — das verzerrt zukuenftige FIFOs.
  // Orphan-Scan nur für den FIFO-Pool (Array von Lots). Der AT-Pool ist anders
  // strukturiert ({ neu, alt }) und kennt kein pendingTransfer.
  const orphans = [];
  if (!useAverage) {
    for (const sym of Object.keys(pool || {})) {
      for (const lot of pool[sym]) {
        if ((lot.pendingTransfer || 0) > 1e-9) {
          orphans.push({ sym, lotId: lot.lotId, pending: lot.pendingTransfer });
        }
      }
    }
  }
  if (orphans.length > 0) {
    warnings.push({
      kind: 'transfer-orphan',
      message: `${orphans.length} Transfer-Reservierung(en) konnten nicht aufgeloest werden. Pruefe Wallet-Bewegungen.`,
      orphans,
    });
  }

  // Bewertung nur in Steuer-Jurisdiktionen (DE/AT). Modell-Abzug = unverbindliche
  // Schätzung (DE 26 %, AT 27,5 %). Protokoll-Länder ('log') → 0.
  const isTax = rules.reportMode === 'tax';
  const taxObligation = isTax ? Math.max(0, taxableGain) * rules.rate : 0;
  const carryLoss = isTax && rules.lossCarryforward && taxableGain < 0 ? Math.abs(taxableGain) : 0;

  return {
    year: year === 'all' || year == null ? 'all' : Number(year),
    isDE,
    country:          rules.code,
    reportMode:       rules.reportMode,
    costBasisMethod:  rules.costBasis,
    rate:             rules.rate,
    incomeEUR:        round2(incomeEUR),
    grossProceeds:    round2(grossProceeds),
    totalFees:        round2(totalFees),
    costBasis:        round2(costBasis),
    realizedPnL:      round2(realizedPnL),
    taxableGain:      round2(taxableGain),
    freeGain:         round2(freeGain),
    taxObligation:    round2(taxObligation),
    carryLoss:        round2(carryLoss),
    eventsCount,
    hasCostBasisUnknown,
    hasFallbackUsed,
    hasIncomplete,
    perAsset:         perAsset.map(roundAsset),
    warnings,
    // Rohzeug fuer Power-User: pool und transferPairs durchreichen,
    // damit Debug-Helfer ohne zweiten FIFO-Lauf auskommen.
    _internal: { pool, transferPairs },
  };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function roundAsset(a) {
  return {
    ...a,
    grossProceeds: round2(a.grossProceeds),
    sellGross:     round2(a.sellGross),
    swapGross:     round2(a.swapGross),
    totalFees:     round2(a.totalFees),
    costBasis:     round2(a.costBasis),
    realizedPnL:   round2(a.realizedPnL),
    taxableGain:   round2(a.taxableGain),
    freeGain:      round2(a.freeGain),
  };
}

/**
 * Bequemer Helfer: liefert alle Berichtsjahre, fuer die TXen existieren,
 * absteigend sortiert, plus das aktuelle Jahr (falls noch keine TX drin).
 */
export function listReportYears(txs) {
  const set = new Set();
  (txs || []).forEach(tx => {
    const d = (tx.datum || tx.effectiveTimestamp || '').slice(0, 4);
    if (d) set.add(Number(d));
  });
  const arr = [...set].sort((a, b) => b - a);
  const cur = new Date().getFullYear();
  if (!arr.includes(cur)) arr.unshift(cur);
  return arr;
}
